import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Dropdown from "@client.components/Dropdown";
import MeetingTypes from "@client.enums/meetingTypes";
import DateTypes from "@client.enums/dateTypes";
import { getTeamsAsync, makeTeams } from "@client.pages/Settings/reducer";
import Button from "components/Button";
import { makeFilter, setFilter } from "./reducer";
import FormatHelper from "@client.utils/FormatHelper";
import Loader from "@client.components/Loader";
import useModal from "@client.hooks/useModal";
import CustomRangeFilter from "./CustomRangeFilter";
import { getDateFrom, getDateTo } from "@client.components/Charts/utils";
import "./TopChartFilters.scss";
import { FilterType } from "./types";
import UserNotificationTypes from "@client.enums/userNotificationTypes";

const TopChartFilters = ({
  type = FilterType.default,
  onExport,
  onShare,
  isExportLoading,
}) => {
  const { t } = useTranslation();
  const filter = useSelector(makeFilter);
  const teams = useSelector(makeTeams);
  const dispatch = useDispatch();
  const [selectedDateType, setSelectedDateType] = useState(filter.dateType);

  useEffect(() => {
    setSelectedDateType(filter.dateType);
  }, [filter.dateType]);

  const onSetFilter = (val) => {
    dispatch(setFilter(val));
  };
  const { showFormModal } = useModal();
  useEffect(() => {
    dispatch(getTeamsAsync());
  }, [dispatch]);

  const getDropdownOption = (value) => {
    const label = FormatHelper.getCountableTermValue(
      t,
      value < 0
        ? "components.TopChartFilters.lastDay"
        : "components.TopChartFilters.nextDay",
      value === DateTypes.Today || value === DateTypes.LastDay ? 1 : value,
      { value: Math.abs(value) }
    );
    return { label, value: value.toString() };
  };

  const renderExportButton = () => {
    if (!onExport) {
      return null;
    }

    return isExportLoading ? (
      <Loader loading={true} />
    ) : (
      <Button
        icon="export"
        text={t("components.TopChartFilters.export")}
        onClick={() => onExport()}
      />
    );
  };

  const onSelectCustomDateRange = (currentFilter) => {
    showFormModal({
      title: t("components.TopChartFilters.title.customRange"),
      contentClass: "custom-date-range",
      component: (
        <CustomRangeFilter
          startDate={currentFilter.dateFrom}
          endDate={currentFilter.dateTo}
        />
      ),
      onClose: () => {
        setSelectedDateType(currentFilter.dateType);
      },
      footerButtons: [
        {
          icon: "pencil",
          onClick: (managementItem) => {
            return new Promise((resolve) => {
              onSetFilter({
                dateType: DateTypes.Custom,
                ...managementItem,
              });

              resolve({});
            });
          },
        },
      ],
    });
  };

  const dateOptions = [
    DateTypes.Last90Days,
    DateTypes.Last30Days,
    DateTypes.Last7Days,
    DateTypes.LastDay,
    DateTypes.Today,
    DateTypes.Next7Days,
  ].map(getDropdownOption);

  dateOptions.push({
    label: "Custom Range",
    value: DateTypes.Custom,
    onClick: () => onSelectCustomDateRange(filter),
  });

  return (
    <div className="top-chart-filters">
      <div className="filters">
        <Dropdown
          items={dateOptions}
          valueKey="value"
          labelKey="label"
          defaultValue={`${selectedDateType}`}
          onSelect={(val) => {
            setSelectedDateType(val);

            if (val !== DateTypes.Custom) {
              onSetFilter({
                dateType: val,
                dateFrom: getDateFrom(Number(val)),
                dateTo: getDateTo(Number(val)),
              });
            }
          }}
        />
        {type !== FilterType.insights && (
          <Dropdown
            items={[
              {
                label: t("components.TopChartFilters.totalMeetingsCost.all"),
                value: MeetingTypes.All,
              },
              {
                label: t("components.Charts.totalMeetingsCost.recurring"),
                value: MeetingTypes.Recurring,
              },
              {
                label: t("components.Charts.totalMeetingsCost.unique"),
                value: MeetingTypes.Unique,
              },
              {
                label: t("components.Charts.totalMeetingsCost.external"),
                value: MeetingTypes.External,
              },
              {
                label: t("components.Charts.totalMeetingsCost.internal"),
                value: MeetingTypes.Internal,
              },
            ]}
            valueKey="value"
            labelKey="label"
            onPrepareValue={(val) => parseInt(val)}
            defaultValue={filter.meetingType}
            onSelect={(val) => {
              onSetFilter({
                meetingType: val,
              });
            }}
          />
        )}
        {type === FilterType.default && (
          <Dropdown
            items={[
              {
                name: t("components.TopChartFilters.allTeams"),
                id: null,
              },
              ...teams,
            ]}
            valueKey="id"
            labelKey="name"
            defaultValue={filter.team}
            onPrepareValue={(v) => (v === "null" ? null : v)}
            onSelect={(val) => {
              onSetFilter({
                team: val,
              });
            }}
          />
        )}
        {type === FilterType.insights && (
          <Dropdown
            items={[
              {
                label: t("pages.Dashboard.Insights.types.all"),
                value: UserNotificationTypes.All,
              },
              {
                label: t("pages.Dashboard.Insights.types.internal"),
                value: UserNotificationTypes.EmployeeOverspendInternal,
              },
              {
                label: t("pages.Dashboard.Insights.types.noOfParticipants"),
                value: UserNotificationTypes.ScheduleOvercrowdedMeeting,
              },
              {
                label: t("pages.Dashboard.Insights.types.highInvestment"),
                value: UserNotificationTypes.ScheduleHighInvestmentMeeting,
              },
              {
                label: t("pages.Dashboard.Insights.types.recurring"),
                value: UserNotificationTypes.ScheduleRecurringMeeting,
              },
              {
                label: t("pages.Dashboard.Insights.types.external"),
                value: UserNotificationTypes.EmployeeOverspendExternal,
              },
              {
                label: t("pages.Dashboard.Insights.types.lowAttendance"),
                value: UserNotificationTypes.LowAttendance,
              },
              {
                label: t("pages.Dashboard.Insights.types.aiMeetingSimilaritiesRecognition"),
                value: UserNotificationTypes.AIMeetingSimilaritiesRecognition,
              },
              {
                label: t("pages.Dashboard.Insights.types.aiPredictions"),
                value: UserNotificationTypes.AIPredictions,
              },
            ]}
            valueKey="value"
            labelKey="label"
            onPrepareValue={(val) => parseInt(val)}
            defaultValue={filter.insightType}
            onSelect={(val) => {
              onSetFilter({
                insightType: val,
              });
            }}
          />
        )}
        {[DateTypes.Today, DateTypes.Next7Days].includes(
          Number(filter.dateType)
        ) && (
          <div className="future-estimations">
            {t("components.TopChartFilters.futureEstimations")}
          </div>
        )}
        {filter.dateType === DateTypes.Custom && (
          <div className="future-estimations">
            {t("components.TopChartFilters.customRange", {
              from: filter.dateFrom,
              to: filter.dateTo,
            })}
          </div>
        )}
      </div>
      <div className="share-export">
        {renderExportButton()}
        {onShare && (
          <Button
            icon="share"
            text={t("components.TopChartFilters.share")}
            onClick={() => onShare()}
          />
        )}
      </div>
    </div>
  );
};

TopChartFilters.propTypes = {
  type: PropTypes.oneOf(Object.values(FilterType)),
  onExport: PropTypes.func,
  onShare: PropTypes.func,
  isExportLoading: PropTypes.bool,
};

export default TopChartFilters;
